import { Link, useNavigate } from 'react-router-dom';
import { CheckCheck, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useUnreadNotifications,
} from '@/hooks/useNotifications';
import type { AppNotification } from '@/types/notification.types';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Sprint 4.D — Panneau déroulant des notifications non lues.
 *
 * - Lit `useUnreadNotifications()` (polling REST 30s)
 * - Clic sur une notif → mark as read + navigate vers `data.actionUrl` (si présent)
 * - Bouton "Tout marquer lu"
 * - Affichage vide explicite si aucune notif
 */
export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const { data: notifications, isLoading } = useUnreadNotifications(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const deleteOne = useDeleteNotification();

  const handleClick = (notif: AppNotification) => {
    markRead.mutate(notif.id);
    if (notif.data?.actionUrl) {
      navigate(notif.data.actionUrl);
      onClose();
    }
  };

  return (
    <div className="flex flex-col max-h-[500px]">
      <header className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">Notifications</h3>
        <div className="flex items-center gap-1">
          {notifications && notifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="h-7 text-xs"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Tout marquer lu
            </Button>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-copper" />
          </div>
        )}

        {!isLoading && (!notifications || notifications.length === 0) && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            Aucune notification non lue
          </div>
        )}

        {!isLoading && notifications && notifications.length > 0 && (
          <ul className="divide-y">
            {notifications.map((notif) => (
              <li key={notif.id} className="group relative">
                <button
                  type="button"
                  onClick={() => handleClick(notif)}
                  className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${typeColor(notif.type)}`}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notif.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {formatRelativeTime(notif.createdAt)}
                      </p>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteOne.mutate(notif.id);
                  }}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
                  aria-label="Supprimer"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Separator />
      <footer className="px-4 py-2">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-xs text-copper hover:underline"
        >
          Voir toutes les notifications
        </Link>
      </footer>
    </div>
  );
}

function typeColor(type: AppNotification['type']): string {
  switch (type) {
    case 'success':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'error':
      return 'bg-red-500';
    case 'opportunity_update':
      return 'bg-copper';
    default:
      return 'bg-blue-500';
  }
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Il y a ${days} j`;
  return date.toLocaleDateString('fr-FR');
}
